import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { services } from '../../../../services';

import { useBackgroundPromiseEffectByCondition } from '../../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { alertSuccess, alertError } from '../../../../store/reducers/Alert';
import { DocumentEditorComponent, DocumentEditorRef } from '../../../../components/actDocumentEditor';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../../helpers/hooks/useDownload';
import { NewButton } from '../../../../components/synced/ui/NewButton/NewButton';

import { Act } from '../../models/Act';
import { FolderFilesActions } from '../folder/FolderFilesActions';
import { ModelAction } from '../../../../types/responses/ModelAction';
import { DataStorage } from '../../../../helpers/utils/DataStorage';
import { ContractorProjectDocFolderActions } from '../../../../helpers/constants/modelActions/ContractorProjectDocFolderActions';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmButton } from '../folder/helpers/ConfirmButton';

interface EdiSigningPackage {
    Id: number;
    Status: number;
    ExpectedExternalSignatureCount: number;
    ReceivedExternalSignatureCount: number;
    IsDocumentLocked: boolean;
    LastError?: string;
}

const ediSigningPackageStatusTitles: { [key: number]: string } = {
    1: 'Черновик',
    2: 'Отправлен провайдеру',
    3: 'Ожидает подписи КА',
    4: 'Подписи КА получены',
    5: 'На подписании X5',
    6: 'Накладка сформирована',
    7: 'Возвращен провайдеру',
    8: 'Завершен',
    9: 'Ошибка'
};

interface ActEditProps {
    docFolderId: number;
    onCurrentFolderStatusUpdated: () => void;
    onDeleted?: () => void;
    dataStorage: DataStorage;
    setDataStorageValue: (key: string, value: any) => void;
}

export const ActEdit: React.FC<ActEditProps> = ({
    docFolderId,
    onCurrentFolderStatusUpdated,
    dataStorage,
    onDeleted,
    setDataStorageValue
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [currentHtmlContent, setCurrentHtmlContent] = React.useState<string>('');
    const [act, setAct] = React.useState<Act>();
    const [actFolderId, setActFolderId] = React.useState<number>();
    const [ediPackage, setEdiPackage] = React.useState<EdiSigningPackage | null>(null);
    const [ediPackageFolderId, setEdiPackageFolderId] = React.useState<number>();
    const [readonly, setReadonly] = React.useState<boolean>(true);
    const [editable, setEditable] = React.useState<boolean>(false);
    const [promiseWrap, isLoading] = useBackgroundPromiseWrapper();
    const editorRef = React.useRef<DocumentEditorRef>(null);

    const [actions, setActions] = React.useState<ModelAction[]>([]);

    const [promiseWrapper] = useBackgroundPromiseWrapper();
    const { downloadFile, isDownloading } = useDownload();
    const dataStorageRef = React.useRef(dataStorage);
    dataStorageRef.current = dataStorage;

    const actionsRequestIdRef = React.useRef(0);
    const latestFolderIdRef = React.useRef(docFolderId);

    React.useEffect(() => {
        latestFolderIdRef.current = docFolderId;
    }, [docFolderId]);

    const loadActions = React.useCallback(
        (force: boolean = false) => {
            const folderId = docFolderId;
            const ds = dataStorageRef.current;
            if (folderId === 0) {
                setActions([]);
                return;
            }

            const actionsKeyForFolder = `ActEditActions_actions_${folderId}`;
            if (!force && ds.hasValue(actionsKeyForFolder)) {
                setActions(ds.get<ModelAction[]>(actionsKeyForFolder, []));
                return;
            }

            const requestId = ++actionsRequestIdRef.current;
            promiseWrapper(
                () => services.projectDocFolderService.actions(folderId),
                (res: ModelAction[]) => {
                    if (requestId !== actionsRequestIdRef.current) {
                        return;
                    }
                    if (latestFolderIdRef.current !== folderId) {
                        return;
                    }
                    ds.set(actionsKeyForFolder, res);
                    setActions(res);
                }
            );
        },
        [docFolderId]
    );

    React.useEffect(() => {
        loadActions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docFolderId]);

    useBackgroundPromiseEffectByCondition(
        () => docFolderId > 0 && actFolderId !== docFolderId,
        () => services.projectDocFolderService.getAct(docFolderId),
        (res: Act) => {
            setAct(res);
            setActFolderId(docFolderId);
        },
        [docFolderId, act, setAct, actFolderId, setActFolderId]
    );

    useBackgroundPromiseEffectByCondition(
        () => docFolderId > 0 && ediPackageFolderId !== docFolderId,
        () => services.projectDocFolderService.getEdiSigningPackage(docFolderId),
        (res: EdiSigningPackage | null) => {
            setEdiPackage(res);
            setEdiPackageFolderId(docFolderId);
        },
        [docFolderId, ediPackageFolderId, setEdiPackage, setEdiPackageFolderId]
    );

    React.useEffect(() => {
        if (act) {
            setCurrentHtmlContent(act.HtmlTemplate || act.ActTemplate?.HtmlTemplate || '');
            document.title = `Акт - ${act.Title}`;
        }
    }, [act]);

    React.useEffect(() => {
        if (actions?.length > 0) {
            const editable =
                actions.some((o) => o.Id === ContractorProjectDocFolderActions.EditAct) &&
                !ediPackage?.IsDocumentLocked;
            setEditable(editable);
            if (!editable || ediPackage?.IsDocumentLocked) setReadonly(true);
        }
    }, [actions, ediPackage?.IsDocumentLocked, setReadonly, setEditable]);

    const [activeIndex, setActiveIndex] = React.useState<number>(0);

    const validateMargin = (value: string): boolean => {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === '0') return true;
        return /^(0|[1-9]\d*)(\.\d+)?(px|em|rem|cm)?$/i.test(trimmed);
    };

    const handleSave = React.useCallback(() => {
        if (!act) return;
        if (ediPackage?.IsDocumentLocked) {
            dispatch(
                alertError({
                    summary: 'Акт заблокирован',
                    detail: 'Акт уже отправлен на ЭДО-подписание и недоступен для изменения'
                })
            );
            return;
        }

        const isEdit = !!act.Id;

        if (!act.Title || act.Title.trim().length === 0) {
            dispatch(alertError({ summary: 'Ошибка валидации', detail: 'Введите наименование акта' }));
            return;
        }

        if (!isEdit && !act.ActTemplateId) {
            dispatch(alertError({ summary: 'Ошибка валидации', detail: 'Выберите шаблон акта' }));
            return;
        }

        const marginTop = act.MarginTop ?? '0';
        const marginRight = act.MarginRight ?? '0';
        const marginBottom = act.MarginBottom ?? '0';
        const marginLeft = act.MarginLeft ?? '0';

        if (
            isEdit &&
            (!validateMargin(marginTop) ||
                !validateMargin(marginRight) ||
                !validateMargin(marginBottom) ||
                !validateMargin(marginLeft))
        ) {
            dispatch(
                alertError({
                    summary: 'Ошибка валидации',
                    detail: 'Проверьте правильность указания отступов (допустимые единицы: px, em, rem, cm)'
                })
            );
            return;
        }

        const actualHtml = editorRef.current?.getCurrentHtml() || currentHtmlContent || act.HtmlTemplate || '';

        const model: Act = {
            ...act,
            ActTemplate: undefined,
            MarginTop: (marginTop || '').trim() || '0',
            MarginRight: (marginRight || '').trim() || '0',
            MarginBottom: (marginBottom || '').trim() || '0',
            MarginLeft: (marginLeft || '').trim() || '0',
            HtmlTemplate: actualHtml
        } as Act;

        promiseWrap(
            () => services.projectDocFolderService.saveAct(docFolderId, model),
            (res) => {
                dispatch(alertSuccess());
                setAct(res);
                setReadonly(true);
            }
        );
    }, [act, ediPackage?.IsDocumentLocked, setAct, currentHtmlContent, navigate, dispatch, editorRef]);

    const onCurrentFolderStatusUpdatedHandler = React.useCallback(() => {
        onCurrentFolderStatusUpdated();
        loadActions(true);
    }, [loadActions, onCurrentFolderStatusUpdated]);

    const handleRefreshSigners = React.useCallback(() => {
        if (ediPackage?.IsDocumentLocked) return;
        promiseWrap(
            () => services.projectDocFolderService.refreshActSigners(docFolderId),
            (res: Act) => {
                dispatch(alertSuccess({ summary: 'Успешно', detail: 'Подписанты обновлены' }));
                setAct(res);
                loadActions(true);
            }
        );
    }, [docFolderId, ediPackage?.IsDocumentLocked, loadActions]);

    return (
        <>
            {act && (
                <>
                    {ediPackage && (
                        <div
                            className={`act-banner ${ediPackage.Status === 9 ? 'act-banner-danger' : 'act-banner-info'}`}
                        >
                            <b>ЭДО:</b> {ediSigningPackageStatusTitles[ediPackage.Status] ?? ediPackage.Status}
                            {ediPackage.IsDocumentLocked ? ' · акт заблокирован' : ''}
                            {` · подписи КА ${ediPackage.ReceivedExternalSignatureCount}/${ediPackage.ExpectedExternalSignatureCount}`}
                            {ediPackage.LastError ? ` · ${ediPackage.LastError}` : ''}
                        </div>
                    )}
                    <div className="act-toolbar">
                        {!readonly && (
                            <NewButton
                                variant="primary"
                                icon="fa-solid fa-floppy-disk"
                                disabled={isLoading}
                                onClick={handleSave}
                            >
                                Сохранить
                            </NewButton>
                        )}
                        {readonly && editable && (
                            <NewButton
                                variant="secondary"
                                icon="fa-solid fa-pencil"
                                disabled={isLoading || !!ediPackage?.IsDocumentLocked}
                                onClick={() => setReadonly(false)}
                            >
                                Редактировать
                            </NewButton>
                        )}
                        <NewButton
                            variant="secondary"
                            icon={isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`}
                            disabled={isLoading || !act?.Id || isDownloading}
                            onClick={() => {
                                const id = act?.Id;
                                if (id) void downloadFile(`Acts/Export?id=${id}`);
                            }}
                        >
                            {isDownloading ? `Скачивание…` : `Скачать акт`}
                        </NewButton>
                        {readonly &&
                            actions.some((o) => o.Id === ContractorProjectDocFolderActions.RefreshActSigners) && (
                                <NewButton
                                    variant="secondary"
                                    icon="fa-solid fa-rotate"
                                    disabled={isLoading}
                                    onClick={handleRefreshSigners}
                                >
                                    Обновить подписантов
                                </NewButton>
                            )}
                        {readonly && (
                            <FolderFilesActions
                                actions={actions ?? []}
                                projectDocFolderId={docFolderId}
                                filesCount={1}
                                onCurrentFolderStatusUpdated={onCurrentFolderStatusUpdatedHandler}
                            />
                        )}
                        {readonly && actions.some((o) => o.Id === ContractorProjectDocFolderActions.DeleteAct) && (
                            <ConfirmButton
                                icon="fa-solid fa-trash"
                                label="Удалить акт"
                                header="Удаление акта"
                                message="Вы уверены, что хотите удалить акт?"
                                acceptLabel="Удалить"
                                acceptIcon="fa-solid fa-trash"
                                disabled={isLoading}
                                action={() => {
                                    promiseWrap(
                                        () => services.projectDocFolderService.delete([docFolderId]),
                                        () => {
                                            dispatch(alertSuccess());
                                            onDeleted?.();
                                        }
                                    );
                                }}
                            />
                        )}
                    </div>
                    <div className="document-editor-fill">
                        <DocumentEditorComponent
                            ref={editorRef}
                            initialHtmlContent={act.HtmlTemplate || act.ActTemplate?.HtmlTemplate || ''}
                            adminMode={false}
                            readonly={readonly}
                            onHtmlContentChange={setCurrentHtmlContent}
                        />
                    </div>
                </>
            )}
        </>
    );
};
